// src/ui/control-panel.ts
import { createSlider } from './controls/slider';
import { createDropdown } from './controls/dropdown';
import { createToggle } from './controls/toggle';
import type { DitherAlgorithm, DitherParams, Palette } from '../core/types';
import { encodePreset } from '../core/presets';

export type Action = 'open' | 'save' | 'export-video' | 'reset' | 'restart' | 'randomize';

export interface OutputPreset {
  id: string;
  label: string;
  group: string;
  downscale: { enabled: boolean; w: number; h: number };
}

export const OUTPUT_PRESETS: OutputPreset[] = [
  { id: 'original', label: 'Original',           group: 'Original',  downscale: { enabled: false, w: 1280, h: 720  } },
  { id: '240p',     label: '240p (320×240)',     group: 'Standard',  downscale: { enabled: true,  w: 320,  h: 240  } },
  { id: '360p',     label: '360p (640×360)',     group: 'Standard',  downscale: { enabled: true,  w: 640,  h: 360  } },
  { id: '480p',     label: '480p (854×480)',     group: 'Standard',  downscale: { enabled: true,  w: 854,  h: 480  } },
  { id: '720p',     label: '720p (1280×720)',    group: 'Standard',  downscale: { enabled: true,  w: 1280, h: 720  } },
  { id: '1080p',    label: '1080p (1920×1080)',  group: 'Standard',  downscale: { enabled: true,  w: 1920, h: 1080 } },
  { id: '1440p',    label: '1440p (2560×1440)',  group: 'Standard',  downscale: { enabled: true,  w: 2560, h: 1440 } },
  { id: '4k',       label: '4K (3840×2160)',     group: 'Standard',  downscale: { enabled: true,  w: 3840, h: 2160 } },
  { id: 'sq256',    label: 'Square 256',         group: 'Square',    downscale: { enabled: true,  w: 256,  h: 256  } },
  { id: 'sq512',    label: 'Square 512',         group: 'Square',    downscale: { enabled: true,  w: 512,  h: 512  } },
  { id: 'sq1024',   label: 'Square 1024',        group: 'Square',    downscale: { enabled: true,  w: 1024, h: 1024 } },
  { id: 'sq2048',   label: 'Square 2048',        group: 'Square',    downscale: { enabled: true,  w: 2048, h: 2048 } },
  { id: 'pa64',     label: 'Pixel art 64',       group: 'Pixel Art', downscale: { enabled: true,  w: 64,   h: 64   } },
  { id: 'pa128',    label: 'Pixel art 128',      group: 'Pixel Art', downscale: { enabled: true,  w: 128,  h: 128  } },
  { id: 'pa256',    label: 'Pixel art 256',      group: 'Pixel Art', downscale: { enabled: true,  w: 256,  h: 256  } },
];

export interface ControlPanelOpts {
  params: DitherParams;
  algorithms: DitherAlgorithm[];
  palettes: Palette[];
  onParamChange: (patch: Partial<DitherParams>) => void;
  onAction: (a: Action) => void;
  onPresetApply?: (code: string) => void;
}

const section = (title: string, ...children: HTMLElement[]) => {
  const s = document.createElement('section');
  const h = document.createElement('h3'); h.textContent = title;
  s.append(h, ...children);
  return s;
};

const button = (label: string, action: Action, onAction: (a: Action) => void) => {
  const b = document.createElement('button');
  b.textContent = label;
  b.dataset.action = action;
  b.addEventListener('click', () => onAction(action));
  return b;
};

const createHeader = (): HTMLElement => {
  const header = document.createElement('header');
  header.className = 'panel-header';
  header.append(document.createTextNode('Dither ExCALiBRator by '));
  const a = document.createElement('a');
  a.href = 'https://t.me/excalibr03';
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.textContent = 'Seva';
  header.append(a);
  return header;
};

const createLogo = (): HTMLImageElement => {
  const img = document.createElement('img');
  img.src = '/logo.png';
  img.alt = 'Dither ExCALiBRator';
  img.className = 'panel-logo';
  img.addEventListener('error', () => { img.style.display = 'none'; });
  return img;
};

const findCurrentPresetId = (downscale: DitherParams['downscale']): string => {
  if (!downscale.enabled) return 'original';
  const match = OUTPUT_PRESETS.find(
    p => p.downscale.enabled && p.downscale.w === downscale.w && p.downscale.h === downscale.h,
  );
  return match ? match.id : 'original';
};

const createOutputSizeDropdown = (
  initial: string,
  onPick: (preset: OutputPreset) => void,
): HTMLDivElement => {
  const wrap = document.createElement('div');
  wrap.className = 'slider-row';
  wrap.style.display = 'block';
  const lab = document.createElement('label');
  lab.textContent = 'Output Size';
  const sel = document.createElement('select');
  sel.style.width = '100%';
  sel.dataset.control = 'outputSize';

  const groups: Record<string, HTMLOptGroupElement> = {};
  const ensureGroup = (name: string): HTMLOptGroupElement => {
    if (!groups[name]) {
      const og = document.createElement('optgroup');
      og.label = name;
      groups[name] = og;
      sel.append(og);
    }
    return groups[name]!;
  };

  for (const p of OUTPUT_PRESETS) {
    const opt = document.createElement('option');
    opt.value = p.id;
    opt.textContent = p.label;
    if (p.id === 'original') {
      sel.append(opt); // Top-level "Original" option, no group
    } else {
      ensureGroup(p.group).append(opt);
    }
  }

  sel.value = initial;
  sel.addEventListener('change', () => {
    const preset = OUTPUT_PRESETS.find(p => p.id === sel.value);
    if (preset) onPick(preset);
  });
  wrap.append(lab, sel);
  return wrap;
};

export function createControlPanel(opts: ControlPanelOpts): HTMLElement {
  const root = document.createElement('aside');
  root.className = 'control-panel';

  const p = opts.params;
  const set = (patch: Partial<DitherParams>) => opts.onParamChange(patch);

  // Top-level header (above all sections)
  const header = createHeader();

  // Logo sits ABOVE the File section header (not inside it).
  const logo = createLogo();

  const exportInfo = document.createElement('div');
  exportInfo.className = 'export-info';
  exportInfo.textContent = 'Video: max 15s, ≤ 50 MB';

  const fileSection = section(
    'File',
    button('Open Image / Video', 'open', opts.onAction),
    button('Save Image', 'save', opts.onAction),
    button('Export Video', 'export-video', opts.onAction),
    exportInfo,
  );

  // Dither section
  const styleDD = createDropdown({
    label: 'Style',
    value: p.styleId,
    options: opts.algorithms.map(a => ({ id: a.id, label: a.label })),
    onChange: id => {
      set({ styleId: id });
      renderTileRow(findAlg(id));
    },
  });
  styleDD.querySelector('select')!.dataset.control = 'styleId';

  const findAlg = (id: string): DitherAlgorithm | undefined =>
    opts.algorithms.find(a => a.id === id);

  // Tile row is dynamic — its visibility and slider range depend on the
  // active algorithm's `tileRange`. We swap children of a placeholder div.
  const tileRow = document.createElement('div');
  tileRow.dataset.row = 'tile';

  const renderTileRow = (alg: DitherAlgorithm | undefined) => {
    tileRow.replaceChildren();
    if (!alg || !alg.tileRange) {
      tileRow.style.display = 'none';
      return;
    }
    tileRow.style.display = '';
    const { min, max } = alg.tileRange;
    const value = Math.max(min, Math.min(max, p.tile));
    tileRow.append(
      createSlider({
        label: 'Tile Size', min, max, step: 1, value,
        onChange: v => set({ tile: v }),
      }),
    );
    if (value !== p.tile) set({ tile: value });
  };

  renderTileRow(findAlg(p.styleId));

  const ditherSection = section(
    'Dither',
    button('🎲 Randomize', 'randomize', opts.onAction),
    styleDD,
    createSlider({ label: 'Scale', min: 1, max: 32, step: 1, value: p.scale,
      onChange: v => set({ scale: v }) }),
    tileRow,
    createDropdown({
      label: 'Palette',
      value: p.paletteId,
      options: opts.palettes.map(pp => ({ id: pp.id, label: pp.label })),
      onChange: id => set({ paletteId: id }),
    }),
  );

  // Adjustments
  const adjSection = section(
    'Adjustments',
    createToggle({ label: 'Invert Output', value: p.invert, onChange: v => set({ invert: v }) }),
    createSlider({ label: 'Contrast',     min: -100, max: 100, step: 1,    value: p.contrast,
      onChange: v => set({ contrast: v }) }),
    createSlider({ label: 'Midtones',     min: 0.1,  max: 3.0, step: 0.01, value: p.midtones,
      onChange: v => set({ midtones: v }) }),
    createSlider({ label: 'Highlights',   min: -100, max: 100, step: 1,    value: p.highlights,
      onChange: v => set({ highlights: v }) }),
    createSlider({ label: 'Blur',         min: 0,    max: 20,  step: 0.1,  value: p.blur,
      onChange: v => set({ blur: v }) }),
    createSlider({ label: 'Lum.Threshold',min: 0,    max: 255, step: 1,    value: p.threshold,
      onChange: v => set({ threshold: v }) }),
    createToggle({
      label: 'Apply Threshold',
      value: p.thresholdEnabled,
      onChange: v => set({ thresholdEnabled: v }),
    }),
  );

  // Output: replaced toggle + W/H sliders with a single preset dropdown.
  const outputSection = section(
    'Output',
    createOutputSizeDropdown(findCurrentPresetId(p.downscale), preset => {
      set({ downscale: { ...preset.downscale } });
    }),
  );

  // Preset section: export current params as a short code, paste a code to apply.
  const presetSection = section(
    'Preset',
    (() => {
      const row = document.createElement('div');
      row.style.cssText = 'display: grid; grid-template-columns: 1fr auto; gap: 6px; margin-bottom: 6px;';
      const codeInput = document.createElement('input');
      codeInput.type = 'text';
      codeInput.readOnly = true;
      codeInput.value = encodePreset(opts.params);
      codeInput.dataset.role = 'preset-code';
      codeInput.style.cssText = 'font-size: 10px; padding: 4px 6px;';
      codeInput.addEventListener('focus', () => codeInput.select());
      const copyBtn = document.createElement('button');
      copyBtn.textContent = 'Copy';
      copyBtn.style.cssText = 'padding: 4px 10px;';
      copyBtn.addEventListener('click', async () => {
        try {
          await navigator.clipboard.writeText(codeInput.value);
          copyBtn.textContent = 'Copied!';
          setTimeout(() => { copyBtn.textContent = 'Copy'; }, 1200);
        } catch {
          codeInput.select();
          // Best-effort fallback for non-secure contexts; deprecated but widely supported.
          try { document.execCommand('copy'); } catch { /* ignore */ }
        }
      });
      row.append(codeInput, copyBtn);
      return row;
    })(),
    (() => {
      const row = document.createElement('div');
      row.style.cssText = 'display: grid; grid-template-columns: 1fr auto; gap: 6px;';
      const pasteInput = document.createElement('input');
      pasteInput.type = 'text';
      pasteInput.placeholder = 'Paste preset code…';
      pasteInput.dataset.role = 'preset-paste';
      pasteInput.style.cssText = 'font-size: 10px; padding: 4px 6px;';
      const applyBtn = document.createElement('button');
      applyBtn.textContent = 'Apply';
      applyBtn.dataset.role = 'preset-apply';
      applyBtn.style.cssText = 'padding: 4px 10px;';
      applyBtn.addEventListener('click', () => {
        const code = pasteInput.value.trim();
        if (!code) return;
        opts.onPresetApply?.(code);
        pasteInput.value = '';
      });
      row.append(pasteInput, applyBtn);
      return row;
    })(),
  );

  const actionsSection = section(
    'Actions',
    button('Reset All', 'reset', opts.onAction),
    button('Restart',   'restart', opts.onAction),
  );

  root.append(header, logo, fileSection, ditherSection, adjSection, outputSection, presetSection, actionsSection);
  return root;
}
